/**
 * Drømmerom mattress bundle selector: the browser side of blocks/dr-bundle-selector.liquid.
 *
 * Handles everything the customer sees change: picking "Kun seng" or the package, the mattress
 * quantity steppers, the (currently switched off) "swap mattress" dialog, the package price in the
 * sticky add-to-cart bar, and the hidden "_Valgt pakke" line-item property.
 *
 * Adding to the cart is NOT done here. assets/product-form.js reads the chosen package from this
 * element when "Legg i handlekurv" is pressed and adds the bed and its add-ons in one request, so
 * a failure in this file can never make the bed go into the cart without its mattress: the
 * package that is preselected server-side is what gets added.
 *
 * The code was moved here unchanged from product-form.js; lookups that used to start from the
 * product form now start from this element.
 */
let radioGroupCount = 0;

class DrBundleSelector extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    const bundleControls = this.querySelectorAll('[data-bundle-package]');
    if (!bundleControls.length) return;

    // The radios are not inside a <form>, so same-named radios anywhere on the page form one group.
    // Quick add renders this block again for the same section (same name), so give each copy its own.
    radioGroupCount += 1;
    bundleControls.forEach((input) => {
      if (input instanceof HTMLInputElement) input.name = `${input.name.replace(/--\d+$/, '')}--${radioGroupCount}`;
    });

    const { signal } = this.#abortController;
    bundleControls.forEach((input) => {
      input.addEventListener('change', this.#syncBundleProperty, { signal });
    });

    this.#setupBundleQuantities();
    this.#setupBundleMattressModal();
    this.#syncBundleProperty();
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#abortController = new AbortController();
  }

  /** Recalculate the selected add-ons from their per-unit prices and discounts. */
  #refreshBundleQuantities() {
    const root = this.querySelector('[data-bundle-selector]');
    if (!root) return;
    const rows = Array.from(root.querySelectorAll('[data-bundle-addon-row]'));
    if (!rows.length) return;
    const mattresses = rows.filter(row => row.dataset.isMattress === 'true');
    const totalQty = mattresses.reduce((sum,row) => sum + Number(row.dataset.quantity),0);
    let price = Number(root.dataset.basePrice || 0);
    let compare = Math.max(price, Number(root.dataset.baseCompare || 0));
    const ids = [];
    const labels = [];
    rows.forEach(row => {
      const qty = Number(row.dataset.quantity || 0);
      const unit = Number(row.dataset.unitPrice || 0);
      const pay = Math.max(0,unit - Number(row.dataset.unitDiscount || 0));
      price += pay * qty;
      compare += unit * qty;
      if (qty > 0) { ids.push(row.dataset.variantId + ':' + qty); labels.push(qty + ' × ' + row.dataset.addonTitle); }
      const count = row.querySelector('[data-bundle-row-count]');
      if (count) count.textContent = qty === 1 ? '' : ' × ' + qty;
      const output = row.querySelector('[data-bundle-quantity-value]');
      if (output) output.textContent = String(qty);
      const minus = row.querySelector('[data-bundle-quantity-step="-1"]');
      if (minus) minus.disabled = qty === 0 || totalQty <= 1;
    });
    const input = root.querySelector('.bundle-option--featured [data-bundle-package]');
    if (input) { input.dataset.bundleVariantIds = ids.join(','); input.dataset.bundleQuantityLabel = labels.join(' + '); }
    const money = cents => {
      const format = root.dataset.moneyFormat || '{{amount_with_comma_separator}} kr';
      return format.replace(/{{\s*(\w+)\s*}}/, (_,token) => {
        const noDecimals = token.includes('no_decimals');
        const comma = token.includes('comma_separator');
        const space = token.includes('space_separator');
        const parts = (cents/100).toFixed(noDecimals ? 0 : 2).split('.');
        const separator = space ? ' ' : comma ? '.' : ',';
        return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g,separator) + (parts[1] ? (comma || space ? ',' : '.') + parts[1] : '');
      }).replace(/<[^>]*>/g,'');
    };
    const set = (selector,value) => root.querySelectorAll(selector).forEach(el => el.textContent = value);
    const savings = Math.max(0,compare-price);
    set('[data-bundle-primary-price]',money(price));
    set('[data-bundle-primary-compare]',money(compare));
    set('[data-bundle-primary-saving-value]',money(savings));
    set('[data-bundle-primary-ribbon-saving]',money(savings));
    set('[data-bundle-primary-savings-text]','Pakketilbud: Spar ' + money(savings));
    set('[data-bundle-primary-percent]',(compare ? Math.round(savings*100/compare) : 0) + '% RABATT');
    const badge = root.querySelector('[data-bundle-addon-thumb] .bundle-option__hero-qty');
    if (badge && mattresses.length === 1) { badge.textContent = totalQty + '×'; badge.hidden = totalQty <= 1; }
    root.querySelectorAll('[data-bundle-mattress-choice]').forEach(card => {
      const qty = Number(mattresses[0]?.dataset.quantity || 1);
      const unit = Number(card.dataset.unitPrice || 0);
      const pay = Math.max(0,unit-Number(card.dataset.unitDiscount || 0));
      const altPrice = Number(root.dataset.basePrice || 0) + pay*qty;
      const altCompare = Math.max(Number(root.dataset.basePrice || 0),Number(root.dataset.baseCompare || 0)) + unit*qty;
      Object.assign(card.dataset,{variantQty:String(qty), addonPrice:money(pay*qty),totalPrice:money(altPrice),displayCompare:money(altCompare),displaySavings:money(altCompare-altPrice),percent:String(altCompare ? Math.round((altCompare-altPrice)*100/altCompare) : 0)});
    });
    this.#syncBundleProperty();
  }

  #setupBundleQuantities() {
    const { signal } = this.#abortController;
    this.querySelectorAll('[data-bundle-quantity-step]').forEach(button => {
      button.addEventListener('click', event => {
        event.preventDefault(); event.stopPropagation();
        const row = button.closest('[data-bundle-addon-row]');
        if (!row) return;
        const rows = Array.from(this.querySelectorAll('[data-bundle-addon-row][data-is-mattress="true"]'));
        const qty = Number(row.dataset.quantity || 0);
        const next = qty + Number(button.dataset.bundleQuantityStep);
        const total = rows.reduce((sum,item) => sum + Number(item.dataset.quantity || 0),0);
        if (next < 0 || total + next - qty < 1) return;
        row.dataset.quantity = String(next);
        const input = this.querySelector('.bundle-option--featured [data-bundle-package]');
        if (input) input.checked = true;
        this.#refreshBundleQuantities();
      }, { signal });
    });
    this.#refreshBundleQuantities();
  }

  /** Wires the "swap mattress" modal: openers, alternative cards, and close controls. */
  #setupBundleMattressModal() {
    const modal = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-bundle-mattress-modal]'));
    if (!modal) return;

    const { signal } = this.#abortController;

    this.querySelectorAll('[data-bundle-mattress-open]').forEach((trigger) => {
      trigger.addEventListener('click', this.#openMattressModal, { signal });
      trigger.addEventListener('keydown', this.#onMattressTriggerKeydown, { signal });
    });

    modal.querySelectorAll('[data-bundle-mattress-choice]').forEach((card) => {
      card.addEventListener('click', this.#chooseMattress, { signal });
    });

    modal.querySelectorAll('[data-bundle-mattress-close]').forEach((closeButton) => {
      closeButton.addEventListener('click', () => modal.close(), { signal });
    });

    // Close when clicking the backdrop (clicks on the dialog element itself, outside its inner panel).
    modal.addEventListener(
      'click',
      (event) => {
        if (event.target === modal) modal.close();
      },
      { signal }
    );
  }

  /** @param {Event} event */
  #openMattressModal = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const modal = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-bundle-mattress-modal]'));
    modal?.showModal?.();
  };

  /** @param {KeyboardEvent} event */
  #onMattressTriggerKeydown = (event) => {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      this.#openMattressModal(event);
    }
  };

  /**
   * Swaps the bundled mattress to the chosen alternative, updating the paired variant id and all
   * price/label displays from values pre-formatted server-side (avoids client-side money formatting).
   * @param {Event} event
   */
  #chooseMattress = (event) => {
    const card = /** @type {HTMLElement} */ (event.currentTarget);
    const data = card.dataset;
    const root = this.querySelector('[data-bundle-selector]');
    if (!root) return;

    const featuredInput = /** @type {HTMLInputElement | null} */ (
      root.querySelector('.bundle-option--featured [data-bundle-package]')
    );
    if (featuredInput && data.variantId) {
      // Preserve the bundle quantity (e.g. bunk beds add the chosen mattress twice).
      const variantQty = Math.max(1, parseInt(data.variantQty || '1', 10) || 1);
      featuredInput.dataset.bundleVariantIds = variantQty > 1 ? `${data.variantId}:${variantQty}` : data.variantId;
    }

    /** @param {string} selector @param {string | undefined} value */
    const setText = (selector, value) => {
      if (value === undefined) return;
      root.querySelectorAll(selector).forEach((el) => {
        el.textContent = value;
      });
    };

    /** @param {string} selector @param {string | undefined} src */
    const setImage = (selector, src) => {
      if (!src) return;
      root.querySelectorAll(selector).forEach((el) => {
        const img = el instanceof HTMLImageElement ? el : el.querySelector('img');
        if (img) {
          img.src = src;
          img.removeAttribute('srcset');
        }
      });
    };

    setText('[data-bundle-addon-name]', data.addonName);
    setText('[data-bundle-addon-price]', data.addonPrice);
    setText('[data-bundle-primary-price]', data.totalPrice);
    if (data.displaySavings !== undefined) {
      setText('[data-bundle-primary-savings-text]', `Pakketilbud: Spar ${data.displaySavings}`);
      setText('[data-bundle-primary-saving-value]', data.displaySavings);
      setText('[data-bundle-primary-ribbon-saving]', data.displaySavings);
    }
    setText('[data-bundle-primary-compare]', data.displayCompare);
    if (data.percent !== undefined) setText('[data-bundle-primary-percent]', `${data.percent}% RABATT`);
    setText('[data-bundle-addon-tooltip-desc]', data.desc);

    setImage('[data-bundle-addon-thumb]', data.thumb);
    setImage('[data-bundle-addon-summary-thumb]', data.thumb);
    setImage('[data-bundle-addon-tooltip-image]', data.image);

    root.querySelectorAll('[data-bundle-mattress-choice]').forEach((other) => {
      other.classList.toggle('is-selected', other === card);
    });

    // Make sure the "bed + mattress" package is active so the chosen mattress is what gets added.
    if (featuredInput && !featuredInput.checked) {
      featuredInput.checked = true;
    }
    const quantityRow = root.querySelector('[data-bundle-addon-row][data-is-mattress="true"]');
    if (quantityRow) {
      Object.assign(quantityRow.dataset,{variantId:data.variantId,unitPrice:data.unitPrice,unitDiscount:data.unitDiscount,addonTitle:data.productTitle});
      // Restore row structure after the legacy model-swap label update.
      const name = quantityRow.querySelector('[data-bundle-addon-name]');
      if (name) {
        name.textContent = '';
        const title = document.createElement('span'); title.dataset.bundleRowTitle = ''; title.textContent = data.productTitle || '';
        const count = document.createElement('span'); count.dataset.bundleRowCount = '';
        name.append(title,count);
      }
    }
    this.#refreshBundleQuantities();
    this.#syncBundleProperty();

    const modal = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-bundle-mattress-modal]'));
    modal?.close();
  };

  #syncBundleProperty = () => {
    const selectedPackage = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-bundle-package]:checked'));
    const bundlePropertyInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-bundle-selection-property]'));
    const bundleSummary = this.querySelector('[data-bundle-live-summary]');

    if (!selectedPackage || !bundlePropertyInput) return;

    const bundleLabel = this.#buildBundleLabel(selectedPackage);

    bundlePropertyInput.value = bundleLabel;
    const root = this.querySelector('[data-bundle-selector]');
    const stickyPrice = document.querySelector('[data-testid="sticky-price-display"] .price-main-row');
    if (root && stickyPrice) {
      const featured = selectedPackage.closest('.bundle-option--featured');
      const current = root.querySelector(featured ? '[data-bundle-primary-price]' : '.bundle-option__base-price');
      if (current) {
        stickyPrice.textContent = '';
        const price = document.createElement('span'); price.className = 'price'; price.textContent = current.textContent;
        stickyPrice.append(price);
        if (featured) {
          const compare = root.querySelector('[data-bundle-primary-compare]');
          if (compare) { const was = document.createElement('s'); was.textContent = compare.textContent; stickyPrice.append(was); }
          const saving = root.querySelector('[data-bundle-primary-saving-value]');
          if (saving) { const badge = document.createElement('span'); badge.className = 'price-discount-badge'; badge.textContent = 'SPAR ' + saving.textContent; stickyPrice.append(badge); }
        }
      }
    }

    if (bundleSummary) {
      bundleSummary.textContent = `${bundleLabel} legges til med ett klikk.`;
    }

    this.dispatchEvent(
      new CustomEvent('bundleSelectionChange', {
        bubbles: true,
        detail: {
          label: bundleLabel,
        },
      })
    );
  };

  /**
   * @param {HTMLInputElement} selectedPackage
   * @returns {string}
   */
  #buildBundleLabel(selectedPackage) {
    if (selectedPackage.dataset.bundleQuantityLabel) return 'Seng + ' + selectedPackage.dataset.bundleQuantityLabel;
    return selectedPackage.dataset.bundlePackageLabel || '';
  }
}

if (!customElements.get('dr-bundle-selector')) {
  customElements.define('dr-bundle-selector', DrBundleSelector);
}
